import { AxiosError, isAxiosError } from 'axios';
import { DateTime } from 'luxon';
import { IntlShape } from 'react-intl';

import {
  BulkCreateWoltSpecialsPayload,
  BulkUpdateSpecialsPeriodPayload,
  Item,
  UserRequirement,
  Category,
  MenuContainer,
} from 'apis/restaurant-api/merchant-admin/menu2/types';
import { isDefined } from 'common/utils';

import { BulkCreateSpecialsFormValues } from './BulkCreateSpecialsModal';
import { ValidBatchCreateSpecialsFormValues, ValidBatchUpdateSpecialsAvailabilityPeriodFormValues } from './validation';
import { buildBulkActionBasePayload } from '../../utils';

export type BulkWoltSpecialErrorCode = 'DISCOUNT_AMOUNT_EXCEEDS_ORIGINAL_PRICE' | 'ITEM_IS_ALREADY_WOLT_SPECIALS';

export type ErrorResponseWithDetails = AxiosError<{
  error: {
    description: string;
    items_errors: {
      [itemId: string]: {
        code: BulkWoltSpecialErrorCode;
        message: 'string';
      }[];
    };
  };
  modified_menu_container: MenuContainer;
}>;

export const isErrorResponseWithDetails = (error: unknown): error is ErrorResponseWithDetails =>
  isAxiosError(error) && !!error.response?.data.error?.items_errors && !!error.response?.data.modified_menu_container;

export const fromFormValuesToBatchUpdateAvailabilityPeriodPayload = (
  formValues: ValidBatchUpdateSpecialsAvailabilityPeriodFormValues,
  selectedItemIds: Set<string>,
): BulkUpdateSpecialsPeriodPayload => ({
  item_ids: [...selectedItemIds],
  availability_period_end: { $date: formValues.availabilityEnd },
});

export const fromFormValuesToBatchCreatePayload = (
  categories: Category[],
  formValues: ValidBatchCreateSpecialsFormValues,
  selectedItemIds: Set<string>,
): BulkCreateWoltSpecialsPayload => {
  const availabilityPeriod =
    formValues.availabilityStart || formValues.availabilityEnd
      ? {
          start: formValues.availabilityStart ? { $date: formValues.availabilityStart } : null,
          end: formValues.availabilityEnd ? { $date: formValues.availabilityEnd } : null,
        }
      : null;

  const basePayload = buildBulkActionBasePayload(categories, selectedItemIds);

  return {
    availability_period: availabilityPeriod,
    discount:
      formValues.discountType === 'none'
        ? null
        : {
            amount: String(formValues.discount),
            type: formValues.discountType,
          },
    original_category_ids: basePayload.category_ids,
    original_item_ids: basePayload.item_ids,
    user_purchase_requirement: formValues.userRequirement || null,
  };
};

export const getUserRequirementOptions = (intl: IntlShape): { value: UserRequirement | ''; label: string }[] => [
  { value: '', label: intl.formatMessage({ id: 'menu-item.ftu-restriction-values.no-restriction' }) },
  { value: 'no_wolt_purchases', label: intl.formatMessage({ id: 'menu-item.ftu-restriction-values.ftu-wolt' }) },
  {
    value: 'no_franchise_purchases',
    label: intl.formatMessage({ id: 'menu-item.ftu-restriction-values.ftu-franchise' }),
  },
  {
    value: 'no_venue_purchases',
    label: intl.formatMessage({ id: 'menu-item.ftu-restriction-values.ftu-venue' }),
  },
];

export const getDiscountTypeOptions = (
  intl: IntlShape,
): { value: BulkCreateSpecialsFormValues['discountType']; label: string }[] => [
  { value: 'none', label: intl.formatMessage({ id: 'menu-item.no-discount' }) },
  {
    value: 'absolute',
    label: intl.formatMessage({ id: 'menu-item.discounted-price' }),
  },
  { value: 'relative-percentage', label: intl.formatMessage({ id: 'menu-item.discount-percentage' }) },
];

export const getInvalidItemsForDiscount = (
  items: Item[],
  itemIds: Set<string>,
  discount: number | string | undefined,
  discountType: 'absolute' | 'relative-percentage' | 'none',
) => {
  if (typeof discount !== 'number' || discountType !== 'absolute') {
    return [];
  }

  return [...itemIds]
    .map(itemId => {
      const item = items.find(item => item.id === itemId);

      if (!item) {
        return undefined;
      }

      const itemBaseprice = Number(item.discounted ? item.discounted.original_price.amount : item.baseprice.amount);

      if (itemBaseprice < discount) {
        return item;
      } else {
        return undefined;
      }
    })
    .filter(isDefined);
};

export const asLocaleNaiveDate = (timestamp: number, timezone: string): Date => {
  const realDate = DateTime.fromMillis(timestamp, { zone: timezone });
  const localeDate = new Date(timestamp);
  localeDate.setDate(realDate.day);
  localeDate.setMonth(realDate.month - 1); // js date starts month at 0
  localeDate.setFullYear(realDate.year);
  return localeDate;
};
